/**
 * Client for the household dashboard API (same origin, `/app/*` — see
 * packages/backend/src/routes/householdApp.ts). The session token lives in
 * localStorage and is sent as the `x-amh-key` custom header (mirrors
 * ai-guidance's reference dashboard's api.ts, which uses `x-admin-key` the
 * same way — chosen there, and here, so the token never collides with
 * `Authorization`, which a reverse proxy may already use for its own
 * basic-auth wall).
 */

const KEY_STORAGE = "amh_session";

export function getSessionToken(): string | null {
  return localStorage.getItem(KEY_STORAGE);
}
export function setSessionToken(token: string): void {
  localStorage.setItem(KEY_STORAGE, token);
}
export function clearSessionToken(): void {
  localStorage.removeItem(KEY_STORAGE);
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public data: unknown,
  ) {
    super(`API ${status}`);
  }
}

async function request(path: string, init?: RequestInit): Promise<unknown> {
  const token = getSessionToken();
  const res = await fetch(`/app${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(token ? { "x-amh-key": token } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (res.status === 401 && token) {
    // Server-side, a 401 clears nothing (see householdApp.ts) — the client
    // is the one that must drop a token it now knows is dead.
    clearSessionToken();
    location.reload();
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, data);
  return data;
}

export interface Me {
  email: string;
  installation: { name?: string };
}

export type ServiceStatus = "trial" | "active" | "suspended" | "cancelled";

export interface ServiceDecision {
  allowed: boolean;
  reason: string;
  trialEndsAt: string | null;
}

export interface LatestReport {
  sentAt: string;
  text: string;
  /** Structured echo of the same figures already inside `text` — null
   * whenever this report's energy confidence was low/absent, never a
   * fabricated number. */
  totalKwh: number | null;
  costEur: number | null;
}

export interface Overview {
  slug: string | null;
  url: string | null;
  service: ServiceDecision;
  sessionActive: boolean;
  tunnelLive: boolean;
  latestReport: LatestReport | null;
}

export interface HouseReport {
  id: string;
  sentAt: string;
  text: string;
  /** Structured echo of the same figures already inside `text` — null
   * whenever this report's energy confidence was low/absent, never a
   * fabricated number. */
  totalKwh: number | null;
  costEur: number | null;
}

export interface ShelvedDevice {
  key: string;
  label: string;
  shelvedAt: string;
}

export interface ShelvedCandidate {
  key: string;
  label: string;
}

export interface NotificationPrefs {
  monthlyReport: boolean;
  initiative: boolean;
  revival: boolean;
  quietHours: { enabled: boolean; startHour: number; endHour: number };
}

export interface TelegramLinkCode {
  code: string;
  expiresAt: string;
}

export type TelegramLinkStatus = "active" | "revoked";

export interface TelegramLink {
  id: string;
  status: TelegramLinkStatus;
  linkedAt: string;
  revokedAt: string | null;
}

export const api = {
  login: (email: string, password: string) =>
    request("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }) as Promise<{ token: string }>,

  /** Best-effort: the caller should clear the local token regardless of
   *  whether this succeeds (mirrors ai-guidance's api.ts's logout). */
  logout: () => request("/auth/logout", { method: "POST" }) as Promise<{ ok: true }>,

  me: () => request("/me") as Promise<Me>,

  overview: () => request("/overview") as Promise<Overview>,

  reports: () => request("/reports") as Promise<{ reports: HouseReport[] }>,

  changePassword: (currentPassword: string, newPassword: string) =>
    request("/auth/change-password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) }) as Promise<{ ok: true }>,

  telegramLinkCode: () => request("/telegram/link-code", { method: "POST" }) as Promise<TelegramLinkCode>,

  telegramLinks: () => request("/telegram/links") as Promise<{ configured: boolean; links: TelegramLink[] }>,

  telegramRevokeLink: (id: string) => request(`/telegram/links/${id}/revoke`, { method: "POST" }) as Promise<{ ok: true }>,

  shelvedDevices: () => request("/shelved") as Promise<{ devices: ShelvedDevice[] }>,

  shelvedCandidates: () => request("/shelved/candidates") as Promise<{ candidates: ShelvedCandidate[]; boxConnected: boolean }>,

  shelveDevice: (key: string, label: string) =>
    request("/shelved", { method: "POST", body: JSON.stringify({ key, label }) }) as Promise<{ devices: ShelvedDevice[] }>,

  unshelveDevice: (key: string) => request(`/shelved/${encodeURIComponent(key)}`, { method: "DELETE" }) as Promise<{ devices: ShelvedDevice[] }>,

  notificationPrefs: () => request("/notification-prefs") as Promise<{ prefs: NotificationPrefs }>,

  setNotificationPrefs: (prefs: NotificationPrefs) =>
    request("/notification-prefs", { method: "PUT", body: JSON.stringify(prefs) }) as Promise<{ prefs: NotificationPrefs }>,
};
